// The add and edit form for one blocked number: number, lists, reason, note and an optional expiry.
// Used on the Blocked and do not call page and in the "Block this number" dialog on calls and contacts,
// so it uses only the shared form classes and the inline stylesheet (no feature CSS).
import type { BlocklistCreate, BlocklistEntry, BlocklistList } from '../../lib/api.ts';
import { parsePhoneInput, phoneParseMessage } from '../../lib/format.ts';
import { BLOCK_LISTS, LIST_HINT, LIST_LABEL, NOTE_MAX, REASON_MAX } from './model.ts';

export interface EntryDraft {
  number: string;
  lists: BlocklistList[];
  reason: string;
  note: string;
  /** yyyy-mm-dd, or '' for no expiry. */
  expires: string;
}

export type DraftField = 'number' | 'lists' | 'reason' | 'note' | 'expires';
export type DraftErrors = Partial<Record<DraftField, string>>;

/** New entries block everything by default; people narrow it down. */
export function emptyDraft(number = '', note = ''): EntryDraft {
  return { number, lists: [...BLOCK_LISTS], reason: '', note, expires: '' };
}

export function draftFromEntry(entry: BlocklistEntry): EntryDraft {
  return { number: entry.e164, lists: [...entry.lists], reason: entry.reason, note: entry.note, expires: entry.expires_at ? localDate(entry.expires_at) : '' };
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** yyyy-mm-dd in the browser's time zone, for a date input. */
export function localDate(iso: string | number): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** An expiry date runs to the end of that day in the browser's time zone. */
export function expiryFromDate(date: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const end = new Date(`${date}T23:59:59`);
  return Number.isNaN(end.getTime()) ? null : end.toISOString();
}

export type DraftResult = { ok: true; body: BlocklistCreate } | { ok: false; errors: DraftErrors };

const E164 = /^\+[1-9][0-9]{7,14}$/;

/**
 * Typed numbers go through libphonenumber. A number already in E.164 (from a call record or an existing
 * entry) is taken as it is, even when the phone-number metadata doesn't know its range.
 */
export function draftNumber(input: string): { ok: true; e164: string } | { ok: false; error: string } {
  const phone = parsePhoneInput(input);
  if (phone.ok) return { ok: true, e164: phone.e164 };
  const trimmed = input.trim();
  return E164.test(trimmed) ? { ok: true, e164: trimmed } : { ok: false, error: phoneParseMessage(phone) };
}

/** What the number field shows for a number handed in as E.164. */
export function numberForField(e164: string): string {
  const phone = parsePhoneInput(e164);
  return phone.ok ? phone.display : e164;
}

export function validateDraft(draft: EntryDraft, now = Date.now()): DraftResult {
  const errors: DraftErrors = {};
  const phone = draftNumber(draft.number);
  if (!phone.ok) errors.number = phone.error;
  const lists = BLOCK_LISTS.filter(l => draft.lists.includes(l));
  if (lists.length === 0) errors.lists = 'Choose at least one: incoming calls, outgoing calls or texts.';
  const reason = draft.reason.replace(/[\u0000-\u001f\u007f]+/g, ' ').trim();
  if (!reason) errors.reason = 'Add a reason, so your team knows why this number is blocked.';
  else if (reason.length > REASON_MAX) errors.reason = `Keep the reason to ${REASON_MAX} characters. Put the rest in the note.`;
  const note = draft.note.replace(/\u0000/g, '');
  if (note.length > NOTE_MAX) errors.note = `Keep the note to ${NOTE_MAX.toLocaleString('en-US')} characters.`;
  let expiresAt: string | null = null;
  if (draft.expires) {
    expiresAt = expiryFromDate(draft.expires);
    if (!expiresAt) errors.expires = 'Enter a date, or choose no expiry.';
    else if (Date.parse(expiresAt) <= now) errors.expires = 'Choose a date after today.';
  }
  if (Object.keys(errors).length > 0 || !phone.ok) return { ok: false, errors };
  return { ok: true, body: { e164: phone.e164, lists, reason, note: note.trim(), expires_at: expiresAt } };
}

/** Field order, for moving focus to the first error. */
export const FIELD_ORDER: readonly DraftField[] = ['number', 'lists', 'reason', 'note', 'expires'];

export function EntryFields({ draft, errors, onChange, idPrefix, numberLocked = false, numberHint }: {
  draft: EntryDraft;
  errors: DraftErrors;
  onChange: (patch: Partial<EntryDraft>) => void;
  idPrefix: string;
  numberLocked?: boolean;
  /** Extra words under the number, for example whether it's already on the list. */
  numberHint?: string | null;
}) {
  const id = (field: DraftField) => `${idPrefix}-${field}`;
  const describedBy = (field: DraftField, hint?: boolean) => [errors[field] ? `${id(field)}-err` : null, hint ? `${id(field)}-hint` : null].filter(Boolean).join(' ') || undefined;
  const phone = draftNumber(draft.number);
  const toggle = (list: BlocklistList, on: boolean) => onChange({ lists: on ? BLOCK_LISTS.filter(l => l === list || draft.lists.includes(l)) : draft.lists.filter(l => l !== list) });
  const today = localDate(Date.now());
  return (
    <>
      <div className="field">
        <label htmlFor={id('number')}>Phone number</label>
        <input
          id={id('number')}
          inputMode="tel"
          autoComplete="off"
          value={numberLocked && phone.ok ? numberForField(phone.e164) : draft.number}
          readOnly={numberLocked}
          onChange={e => onChange({ number: e.target.value })}
          aria-invalid={!!errors.number || undefined}
          aria-describedby={describedBy('number', true)}
        />
        {errors.number ? <p id={`${id('number')}-err`} className="field-error">{errors.number}</p> : null}
        <p id={`${id('number')}-hint`} className="field-hint">
          {numberHint ?? (phone.ok ? <>Saved as <span className="num">{phone.e164}</span>.</> : 'Numbers without a country code are read as US numbers.')}
        </p>
      </div>
      <fieldset className="field-group" aria-describedby={errors.lists ? `${id('lists')}-err` : undefined}>
        <legend>Block</legend>
        {BLOCK_LISTS.map(list => (
          <label key={list} className="check bl-check">
            <input type="checkbox" id={list === BLOCK_LISTS[0] ? id('lists') : undefined} checked={draft.lists.includes(list)} onChange={e => toggle(list, e.target.checked)} />
            <span className="bl-check-text"><strong>{LIST_LABEL[list]}</strong><span className="meta">{LIST_HINT[list]}</span></span>
          </label>
        ))}
        {errors.lists ? <p id={`${id('lists')}-err`} className="field-error">{errors.lists}</p> : null}
      </fieldset>
      <div className="field">
        <label htmlFor={id('reason')}>Reason</label>
        <input id={id('reason')} value={draft.reason} maxLength={REASON_MAX + 20} onChange={e => onChange({ reason: e.target.value })} aria-invalid={!!errors.reason || undefined} aria-describedby={describedBy('reason', true)} autoComplete="off" required />
        {errors.reason ? <p id={`${id('reason')}-err`} className="field-error">{errors.reason}</p> : null}
        <p id={`${id('reason')}-hint`} className="field-hint">Shown on blocked calls and in the list. Up to {REASON_MAX} characters.</p>
      </div>
      <div className="field">
        <label htmlFor={id('note')}>Note <span className="optional">(optional)</span></label>
        <textarea id={id('note')} value={draft.note} onChange={e => onChange({ note: e.target.value })} aria-invalid={!!errors.note || undefined} aria-describedby={describedBy('note', true)} rows={3} />
        {errors.note ? <p id={`${id('note')}-err`} className="field-error">{errors.note}</p> : null}
        <p id={`${id('note')}-hint`} className="field-hint">The history behind the block: what happened, who asked, any reference.</p>
      </div>
      <div className="field">
        <label htmlFor={id('expires')}>Expires <span className="optional">(optional)</span></label>
        <input id={id('expires')} type="date" value={draft.expires} min={today} onChange={e => onChange({ expires: e.target.value })} aria-invalid={!!errors.expires || undefined} aria-describedby={describedBy('expires', true)} />
        {errors.expires ? <p id={`${id('expires')}-err`} className="field-error">{errors.expires}</p> : null}
        <p id={`${id('expires')}-hint`} className="field-hint">{draft.expires ? 'The block ends after this day.' : 'Leave empty to keep the block until someone removes it.'}</p>
      </div>
    </>
  );
}
